import { Account } from '~/types/account';
import { Category, CategoryFilterValue, Subcategory } from '~/types/category';
import { Company } from '~/types/company';
import { DocumentType } from '~/types/document';
import { PaymentMethod } from '~/types/flow';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { TransactionType } from '~/types/transaction';

export enum TransactionRuleDirection {
  Inflow = 'INFLOW',
  Outflow = 'OUTFLOW',
}

export enum DocumentRuleDirection {
  Issued = 'ISSUED',
  Received = 'RECEIVED',
}

type RuleDirection = TransactionRuleDirection | DocumentRuleDirection;

export const RulesKeys = {
  all: ['Rules'],
  infiniteByDirection: (
    companyId: string,
    ruleType: RuleType,
    direction: RuleDirection | null,
    filters?: TransactionsRulesFiltersFields,
  ) => ['Rules', companyId, 'Infinite', ruleType, direction, ...(filters ? [filters] : [])],
  byType: (companyId: string, ruleType: RuleType) => ['Rules', 'All', companyId, ruleType],
  single: (companyId: string, ruleId: string) => ['Rules', companyId, 'Single', ruleId],
};

export const RuleResourceType = 'rule';

export interface RulesMeta {
  page: MetaDataCursorPage;
  total: number;
}

export enum RuleConditionType {
  Keywords = 'KEYWORDS',
  Account = 'ACCOUNT',
  Counterparts = 'COUNTERPARTS',
  TransactionType = 'TYPE',
  DocumentType = 'DOCUMENT_TYPE',
  PaymentMethod = 'PAYMENT_METHOD',
}

export enum RuleSource {
  User = 'USER',
  Proposed = 'PROPOSED',
}

export enum RuleActionType {
  SetCategory = 'SET_CATEGORY',
  Hide = 'HIDE',
  SetFlowAccount = 'SET_ACCOUNT',
  Verify = 'VERIFY',
  AutoReconciliation = 'AUTO_RECONCILIATION',
}
export interface RuleConditionKeywords {
  keywords: string[];
}

interface RuleConditionAccounts {
  ids: Account['id'][];
}

export interface RuleConditionTransactionTypes {
  types: TransactionType[];
}

export interface RuleConditionDocumentTypes {
  types: DocumentType[];
}

export interface RuleConditionPaymentMethods {
  methods: PaymentMethod[];
}

interface RuleActionSetCategory {
  category_id: Category['id'];
  subcategory_id: Subcategory['id'] | null;
}

export interface RuleActionAutoReconciliation {
  account_id: Account['id'];
}

interface RuleUser {
  firstName: string;
  lastName: string;
}

export enum RuleType {
  Transaction = 'TRANSACTION',
  Document = 'DOCUMENT',
}

interface BaseRule {
  id: string;
  priority: number;
  source: RuleSource;
  company: {
    id: Company['id'];
  };
  createdBy: RuleUser;
  updatedBy?: RuleUser;
  createdAt: string;
  updatedAt?: string;
}

interface SetCategoryAction {
  type: RuleActionType.SetCategory;
  value: RuleActionSetCategory;
}

interface HideAction {
  type: RuleActionType.Hide;
}

interface VerifyAction {
  type: RuleActionType.Verify;
}

type TransactionAction = SetCategoryAction | HideAction | VerifyAction;

interface AutoReconciliation {
  type: RuleActionType.AutoReconciliation;
  value: RuleActionAutoReconciliation;
}

type DocumentAction = AutoReconciliation;

interface AccountCondition {
  type: RuleConditionType.Account;
  value: RuleConditionAccounts;
}

interface KeywordsCondition {
  type: RuleConditionType.Keywords;
  value: RuleConditionKeywords;
}

interface TransactionTypeCondition {
  type: RuleConditionType.TransactionType;
  value: RuleConditionTransactionTypes;
}

interface DocumentTypeCondition {
  type: RuleConditionType.DocumentType;
  value: RuleConditionDocumentTypes;
}

interface PaymentMethodCondition {
  type: RuleConditionType.PaymentMethod;
  value: RuleConditionPaymentMethods;
}

type Condition =
  | AccountCondition
  | KeywordsCondition
  | TransactionTypeCondition
  | DocumentTypeCondition
  | PaymentMethodCondition;

export interface TransactionRule extends BaseRule {
  direction: TransactionRuleDirection;
  ruleType: RuleType.Transaction;
  actions: TransactionAction[];
  conditions: Condition[];
}

export interface DocumentRule extends BaseRule {
  direction: DocumentRuleDirection;
  ruleType: RuleType.Document;
  actions: DocumentAction[];
  conditions: Condition[];
}

export type TransactionRuleCreate = Omit<
  TransactionRule,
  'id' | 'priority' | 'updatedBy' | 'createdBy' | 'updatedAt' | 'createdAt'
>;

export type TransactionRuleUpdate = Omit<
  TransactionRule,
  'id' | 'ruleType' | 'source' | 'updatedBy' | 'createdBy' | 'updatedAt' | 'createdAt'
>;

export type DocumentRuleCreate = Omit<
  DocumentRule,
  'id' | 'priority' | 'updatedBy' | 'createdBy' | 'updatedAt' | 'createdAt'
>;

export type DocumentRuleUpdate = Omit<
  DocumentRule,
  'id' | 'ruleType' | 'source' | 'updatedBy' | 'createdBy' | 'updatedAt' | 'createdAt'
>;

export interface ApplyRuleToHistoryPayload {
  applyTo: 'ALL' | 'UNCATEGORISED';
}

export interface ApplyRuleToHistoryResponse {
  categorisedTransactions: number;
}

export interface TransactionsRulesFiltersFields {
  search: string;
  category: CategoryFilterValue | null;
}
